import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Plus, Trash2, Image, Loader2 } from 'lucide-react';

const ServicesPagePanel = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_images: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/services-page-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          hero_title: data.hero_title || '',
          hero_subtitle: data.hero_subtitle || '',
          hero_images: data.hero_images || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/services-page-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(settings)
        }
      );
      
      if (response.ok) {
        alert('Settings saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setSettings(prev => ({
        ...prev,
        hero_images: [...prev.hero_images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setSettings(prev => ({
      ...prev,
      hero_images: prev.hero_images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Services Page Settings</h3>
          <p className="text-gray-500 text-sm">Customize the services page hero section</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#320547] hover:bg-[#95228A]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 space-y-6">
        {/* Hero Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title</label>
          <Input
            value={settings.hero_title}
            onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
            placeholder="Services that drive growth"
            className="text-lg"
          />
        </div>

        {/* Hero Subtitle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
          <Textarea
            value={settings.hero_subtitle}
            onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
            placeholder="We deliver end-to-end technology solutions..."
            rows={3}
          />
        </div>

        {/* Hero Images */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hero Images (Slideshow)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Add multiple images to create an auto-rotating slideshow in the hero section
          </p>

          {/* Add new image */}
          <div className="flex gap-2 mb-4">
            <Input
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="https://images.unsplash.com/..."
              className="flex-1"
            />
            <Button onClick={addImage} variant="outline" className="shrink-0">
              <Plus size={16} className="mr-1" /> Add Image
            </Button>
          </div>

          {/* Image list */}
          {settings.hero_images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {settings.hero_images.map((url, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-200">
                    <img 
                      src={url} 
                      alt={`Hero ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    {index + 1}
                  </div>
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <Image size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">No images added yet</p>
              <p className="text-gray-400 text-xs">Add image URLs above to create a hero slideshow</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-gray-900 rounded-xl p-6 text-white">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Preview</p>
        <h2 className="text-3xl font-bold mb-2">{settings.hero_title || 'Services that drive growth'}</h2>
        <p className="text-gray-400">{settings.hero_subtitle || 'Your subtitle will appear here...'}</p>
        {settings.hero_images.length > 0 && (
          <p className="text-xs text-gray-500 mt-4">{settings.hero_images.length} image(s) in slideshow</p>
        )}
      </div>
    </div>
  );
};

export default ServicesPagePanel;
